import React, { useEffect, useState } from "react";
import { useEffectOnce } from "../hooks/useeffectonce";
import { useDispatch, useSelector } from "react-redux";
import { setFaceIO, setLanguage } from "../redux/slices/appslice";
import { addNotification } from "../redux/slices/notificationsslice";
import {
  setInitialState,
  setPageLoaded,
  login,
  setProcessing,
  createUserPasswordResetLink,
  disposePage,
} from "../redux/slices/loginpageslice";
import { setAppearance } from "../redux/slices/appearanceslice";
import { setCurrentTheme } from "../redux/slices/currentthemeslice";
import { setCurrentUser } from "../redux/slices/currentuserslice";
import { setCounter } from "../redux/slices/notificationscounterslice";
import { useNavigate, useLocation } from "react-router-dom";
import { api, apiErrorsHandler, getErrorMessages } from "../api/httpclient";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { FormInput, FormMultiSelect } from "../forms/form-components";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import {
  isEmptyObject,
  notificationDurationMiddle,
  notificationDurationLong,
  transformReportsMenu,
  isValidEmail,
} from "../lib/utils";
import { themes } from "../sass/themes";
import { APP_HEADER_HEIGHT, FederationId } from "../settings";
import { setSignalRLinkedCompleted } from "../redux/slices/appinitslice";
import bgImg from "../images/agency-main-bg.jpg";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

let faceio;

export const LoginPage = (props) => {
  const dispatch = useDispatch();
  const pageState = useSelector((state) => state.loginPage);
  const currentUser = useSelector((state) => state.currentUser);
  const currentTheme = useSelector((state) => state.currentTheme);
  const appearance = useSelector((state) => state.appearance);
  const app = useSelector((state) => state.app);
  const signalR = useSelector((state) => state.signalR);

  const navigate = useNavigate();
  const location = useLocation();

  const [loginType, setLoginType] = useState(1);
  const [animate, setAnimate] = useState(false);

  const [forgetPassword, setForgetPassword] = useState(false);
  const [forgetPasswordEmail, setForgetPasswordEmail] = useState("");

  const { t } = useTranslation();

  const formValidator = (values) => {
    let result = {};

    let username = values.username;
    let password = values.password;

    if (!username) result.username = t("login.username_error");

    if (!password) result.password = t("login.password_error");

    return isEmptyObject(result) ? undefined : result;
  };

  useEffectOnce(() => {
    //an auth user shall not see this page and must be redirected to /
    if (currentUser.IsAuthenticated) navigate("/", { replace: true });

    dispatch(setPageLoaded());

    // if (!app.faceIO)
    //     dispatch(setFaceIO(new window.faceIO("fioadc50")));

    return () => {
      dispatch(disposePage());
    };
  }, []);

  const handleLogin = (formRendererProps) => {
    let username = formRendererProps.valueGetter("username");
    let password = formRendererProps.valueGetter("password");
    let connectionId = signalR.connectionId;

    if (formValidator({ username, password }) !== undefined) return;

    dispatch(login(username, password, null, null, t)).then(
      (response) => {
        dispatch(setProcessing({ processing: false }));
        var _response = response;

        setTimeout(() => {
          //console.log("LOGGING IN ", _response);

          let destination = "/";
          if (location.state?.from?.pathname) {
            destination = location.state.from.pathname;

            if (location.state.from.search)
              destination += "?" + location.state.from.search;
          }

          //it's ok to dispatch here
          dispatch(
            setCurrentUser({
              ..._response.data.Data,
              ReportsMenu: transformReportsMenu(
                _response.data.Data.ReportsMenu,
              ),
            }),
          );

          if (FederationId != "") {
            //set the theme
            dispatch(setCurrentTheme(themes["2"]));

            //set isRigthToLeft
            dispatch(setAppearance({ isRightToLeft: true }));
            navigate(destination, { replace: true });
          }

          if (FederationId == "") {
            //set the theme
            dispatch(
              setCurrentTheme(themes[_response.data.Data.ThemeId.toString()]),
            );

            let lang = "en";
            switch (_response.data.Data.LanguageId) {
              case 1:
                lang = "en";
                break;
              case 2:
                lang = "ar";
                break;
              case 3:
                lang = "fr";
                break;
              default:
                lang = "en";
            }

            dispatch(setLanguage(lang));
            dispatch(
              setAppearance({
                isRightToLeft: _response.data.Data.IsRightToLeft,
              }),
            );

            //unread notifications
            dispatch(setCounter(_response.data.Data?.UnreadNotificationsCount));

            navigate(destination, { replace: true });

            //connect SignalR F&F
            let userId = _response.data.Data.Id;
            let tenantId = _response.data.Data.Subscription.Id;

            api({
              url: "signalr/connectsignalr",
              method: "post",
              data: {
                userId: userId,
                tenantId: tenantId,
              },
              //headers:{'h':'1'}
            })
              .then((e) => dispatch(setSignalRLinkedCompleted(true)))
              .catch((e) => dispatch(setSignalRLinkedCompleted(true)));
          }
        }, 750);

        setAnimate(true);
      },
      (err) => {
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          let errorMessages = payload?.data?.ErrorMessages;

          if (!errorMessages || errorMessages.length == 0)
            errorMessages = [t("login.errormessage")];
          else errorMessages = getErrorMessages(errorMessages, 1);

          dispatch(
            addNotification({
              text: errorMessages[0],
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("login.errormessage");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
  };

  const handleCreateUserPasswordResetLink = () => {
    if (
      !forgetPasswordEmail ||
      forgetPasswordEmail.trim() == "" ||
      !isValidEmail(forgetPasswordEmail)
    )
      return;

    dispatch(createUserPasswordResetLink(forgetPasswordEmail, t)).then(
      (response) => {
        dispatch(setProcessing({ processing: false }));

        dispatch(
          addNotification({
            text: t("login.forgotpassword-message"),
            type: "success",
            closable: true,
            duration: notificationDurationLong,
            position: "topRight",
          }),
        );

        navigate("/", { replace: true });
      },
      (err) => {
        dispatch(setProcessing({ processing: false }));

        const handleResponseReceived = (state, dispatch, navigate, payload) => {
          dispatch(
            addNotification({
              text: t("login.errormessage"),
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
        };

        const handleUnhandled = (state, dispatch, navigate, payload) => {
          let errorMessage = t("login.errormessage");
          dispatch(
            addNotification({
              text: errorMessage,
              type: "error",
              closable: true,
              duration: notificationDurationMiddle,
              position: "topRight",
            }),
          );
        };

        apiErrorsHandler({}, dispatch, navigate, err, null, {
          responseReceived: handleResponseReceived,
          unhandled: handleUnhandled,
        });
      },
    );
  };

  const handleFaceLogIn = async () => {
    try {
      let response = await app.faceIO.authenticate({
        locale: "auto",
      });

      const faceId = response.facialId;
      const email = response.payload.email;
      const pin = response.payload.pin;

      //try to login
      dispatch(login(null, null, faceId, pin, t)).then(
        (response) => {
          dispatch(setProcessing({ processing: false }));

          var _response = response;

          setTimeout(() => {
            // dispatch(setCurrentUser(payload.data.Data));
            let destination = "/";
            if (location.state?.from?.pathname) {
              destination = location.state.from.pathname;

              if (location.state.from.search)
                destination += "?" + location.state.from.search;
            }

            //it's ok to dispatch here
            dispatch(
              setCurrentUser({
                ..._response.data.Data,
                ReportsMenu: transformReportsMenu(
                  _response.data.Data.ReportsMenu,
                ),
              }),
            );

            //set the theme
            dispatch(
              setCurrentTheme(themes[_response.data.Data.ThemeId.toString()]),
            );

            //unread notifications
            dispatch(setCounter(_response.data.Data.UnreadNotificationsCount));

            navigate(destination, { replace: true });
          }, 750);

          setAnimate(true);
        },
        (err) => {
          dispatch(setProcessing({ processing: false }));

          const handleResponseReceived = (
            state,
            dispatch,
            navigate,
            payload,
          ) => {
            let errorMessages = payload?.data?.ErrorMessages;

            if (!errorMessages || errorMessages.length == 0)
              errorMessages = t("login.errormessage");
            else errorMessages = getErrorMessages(errorMessages, 1);

            dispatch(
              addNotification({
                text: errorMessages[0],
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          };

          const handleUnhandled = (state, dispatch, navigate, payload) => {
            let errorMessage = t("login.errormessage");
            dispatch(
              addNotification({
                text: errorMessage,
                type: "error",
                closable: true,
                duration: notificationDurationMiddle,
                position: "topRight",
              }),
            );
          };

          apiErrorsHandler({}, dispatch, navigate, err, null, {
            responseReceived: handleResponseReceived,
            unhandled: handleUnhandled,
          });
        },
      );
    } catch (error) {
      console.log("Face Error > ", error);
      dispatch(
        addNotification({
          text: t("login.signin_error"),
          type: "error",
          closable: true,
          duration: notificationDurationMiddle,
          position: "topRight",
        }),
      );
    }
  };

  return (
    <div
      className="login-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1 }}
      style={{ backgroundImage: `url(${bgImg})` }}
    >
      <div className="layer-outer">
        <div className="layer-inner"></div>
      </div>
      {pageState.processing && (
        <div className="page-loader">
          <Loader
            size={"large"}
            type="infinite-spinner"
            themeColor={"primary"}
          />
          {pageState.processingText && (
            <div style={{ fontSize: 13, color: currentTheme.primary }}>
              {pageState.processingText}
            </div>
          )}
        </div>
      )}
      {!pageState.processing && (
        <div
          style={{ height: loginType == 2 ? "90%" : "auto" }}
          className={`login-form-wrapper ${animate ? "login-animate" : null}`}
        >
          <div
            style={{
              // color: currentTheme.appBarHomeBg,
              display: "flex",
              alignItems: "center",
              color: "#fff",
              justifyContent: "center",
              marginBottom: 0,
              padding: " 16px 0px",
              fontSize: 18,
              fontWeight: 600,
              backgroundColor: "#ff9600",
            }}
          >
            {forgetPassword ? t("login.resetpassword") : t("login.login")}
          </div>
          {/* <div className="selections">                             
                    <div className="face-pin" onClick={e => {
                        setLoginType(2);
                        handleFaceLogIn();
                    }

                    }>
                        Face / Pin Login
                    </div>
                    {
                        loginType == 1 && 
                        <div className="username-password" onClick={e => setLoginType(1)}>
                            Username / Password Login
                        </div>
                    }  
                </div> */}
          {!forgetPassword && loginType == 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
              }}
            >
              <Form
                autoComplete={"new-password"}
                validator={formValidator}
                initialValues={{
                  username: "",
                  password: "",
                }}
                render={(formRenderProps) => (
                  <FormElement horizontal={false}>
                    <Field
                      id={"username"}
                      name={"username"}
                      label={t("login.username")}
                      component={FormInput}
                      autoFocus={true}
                      autoComplete="new-password"
                    />

                    <Field
                      id={"password"}
                      name={"password"}
                      label={t("login.password")}
                      type="password"
                      component={FormInput}
                      autoComplete="new-password"
                    />

                    <div
                      className="k-form-buttons"
                      style={{ justifyContent: "space-between" }}
                    >
                      <Button
                        style={{ padding: "6px 28px", borderRadius: "0" }}
                        type="reset"
                        onClick={(e) => {
                          e.preventDefault();
                          formRenderProps.onFormReset();
                        }}
                      >
                        {t("login.clear")}
                      </Button>
                      <Button
                        style={{ padding: "6px 28px", borderRadius: "0" }}
                        themeColor={"primary"}
                        onClick={(e) => {
                          e.preventDefault();
                          handleLogin(formRenderProps);
                        }}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit}
                      >
                        {t("login.login")}
                      </Button>
                    </div>
                    <div className="forgot-password" style={{ marginTop: 20 }}>
                      <a
                        href="#"
                        style={{
                          marginTop: 4,
                          textDecoration: "none",
                          color: currentTheme.primary,
                        }}
                        onClick={(e) => {
                          setForgetPasswordEmail("");
                          setForgetPassword(true);
                        }}
                      >
                        {t("login.forgotpassword")}
                      </a>
                    </div>
                  </FormElement>
                )}
              />
            </div>
          )}
          {forgetPassword && (
            <div style={{ padding: 36 }}>
              <div style={{ marginTop: 2, fontSize: 13 }}>
                {t("login.email_error")}
              </div>
              <div style={{ marginTop: 4 }}>
                <Input
                  style={{
                    borderRadius: 0,
                  }}
                  autoFocus={true}
                  type="email"
                  value={forgetPasswordEmail}
                  onChange={(e) => setForgetPasswordEmail(e.value)}
                />
              </div>
              <div
                style={{
                  marginTop: 18,
                  display: "flex",
                  justifyContent: "space-between",
                  alignContent: "center",
                }}
              >
                <Button
                  disabled={
                    pageState.processing ||
                    !forgetPasswordEmail ||
                    forgetPasswordEmail.trim() == "" ||
                    !isValidEmail(forgetPasswordEmail)
                  }
                  onClick={handleCreateUserPasswordResetLink}
                  style={{
                    borderRadius: 0,
                  }}
                >
                  {t("login.submit")}
                </Button>
                <Button
                  style={{
                    borderRadius: 0,
                  }}
                  disabled={pageState.processing}
                  onClick={(e) => {
                    setForgetPassword(false);
                    setForgetPasswordEmail("");
                  }}
                >
                  {t("login.backtologin")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      <style>
        {`
                .login-page
                {
                    display: flex;
                    height: 100%;
                    align-items: center;
                    flex: 1;
                    justify-content: center;
                    position: relative;
                    background-color: ${currentTheme.appBarHomeBg};  
                    direction: ${
                      appearance.isRightToLeft ? "rtl" : "ltr"
                    }                  
                }

                .login-pager .page-loader
                {
                    position: absolute;
                    z-index: 10005;
                    top: calc(50% - ${APP_HEADER_HEIGHT}px); 
                    left: calc(50% - 120px);
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    padding: 20px;
                    background-color: ${currentTheme.componentBg};
                    border: 1px solid ${currentTheme.componentBorderColor};
                    border-radius: 6px;
                }

                .login-page .layer-outer
                {
                    position: absolute;
                    z-index: 3;    
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background-image:linear-gradient(to top, #ff9600 0%, #ff9600 3%, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0) 80%);
                }

                .login-page .layer-inner
                {
                    background-image: linear-gradient(15deg, #ff9600 0%, #ff9600 5%, transparent 30%, transparent 80%) !important;
                    width: 100%;
                    height: 100%;
                    top: 0;
                    left: 0;
                }


                .login-page .login-form-wrapper
                {
                    z-index: 99;
                    width: ${loginType == 1 ? "500px" : "1000px"};
                    // border: 1px solid #E6E6E6;
                    border-radius: 0px;
                    padding: 0;
                    display: flex;
                    flex-direction: column;;
                    align-items: stretch;
                    min-height: ${
                      forgetPassword ? 0 : loginType == 1 ? "400px" : "650px"
                    };
                    background-color: ${currentTheme.componentBg};
                    margin-bottom: 42px;
                }

                .login-page .login-form-wrapper .k-form
                {
                    /*width: 350px;*/
                }

                .login-page .login-form-wrapper .selections
                {
                    padding: 8px;
                    border: 1px solid #E6E6E6;
                    border-top-left-radius: 8px;
                    border-top-right-radius: 8px;
                    display: flex;
                    flex-direction: row-reverse;
                    justify-content: space-between;
                    align-items: center;
                   /*background-color: #4E9FFF;*/
                   background-image: linear-gradient(to right, #F5F5F5, #E6E6E6);
                }

                .login-page .login-form-wrapper .selections .username-password
                {
                    /*background-color: ${
                      loginType == 1 ? currentTheme.appBarBg : "transparent"
                    };*/
                    color: ${
                      loginType == 1
                        ? currentTheme.primary
                        : currentTheme.componentTextColor
                    };
                    font-size: 14px;
                    font-weight: ${loginType == 1 ? 700 : 400};
                    padding: 8px 18px;
                    /*border: 1px solid #4E9FFF;*/
                    border-top-left-radius: 8px;
                }

                .login-page .login-form-wrapper .selections .username-password:hover,
                .login-page .login-form-wrapper .selections .face-pin:hover
                {
                    cursor: pointer;
                    color: ${currentTheme.primary};
                    font-weight: 700;
                }

                .login-page .login-form-wrapper .selections .face-pin
                {
                    /*background-color: ${
                      loginType == 2 ? currentTheme.appBarBg : "transparent"
                    };*/
                    color: ${
                      loginType == 2
                        ? currentTheme.primary
                        : currentTheme.componentTextColor
                    };
                    font-size: 14px;
                    font-weight: ${loginType == 2 ? 700 : 400};
                    padding: 8px 18px;
                    /*border-right: 1px solid ${currentTheme.appBarBg};*/
                    border-top-right-radius: 8px;
                }

                .login-page .k-form .k-form-field:nth-child(2)
                {
                    margin-top:28px;
                }

                .login-page .k-form .k-input
                {
                    padding: 8px;
                    margin-top: 4px;
                    width: 400px;
                    border-radius:0;
                }

                .login-page .login-animate
                {
                    /*animation: login 1s infinite;*/
                    -moz-transform: scale(0.0,0.0) rotate(0deg);
                    -webkit-transform: scale(0.0,0.0) rotate(0deg);
                    transform: scale(0.0,0.0) rotate(0deg);
                    transition-duration: 750ms;
                } 

                .login-page .forgot-password a:hover
                {
                    color: ${currentTheme.error}!important;
                }

                @media (max-width: 767px)
                {
                    .login-page
                    {
                        min-height: 100dvh;
                        height: auto;
                        padding: 24px 16px;
                        align-items: flex-start;
                        box-sizing: border-box;
                    }

                    .login-page .login-form-wrapper
                    {
                        width: min(100%, 500px) !important;
                        min-height: 0;
                        margin: 32px auto;
                    }

                    .login-page .login-form-wrapper .k-form{
                      padding:15px;
                    }

                    .login-page .login-form-wrapper .k-form,
                    .login-page .login-form-wrapper .k-form-element,
                    .login-page .login-form-wrapper .k-form-field-wrap,
                    .login-page .k-form .k-input
                    {
                        width: 100% !important;
                        max-width: 100%;
                        box-sizing: border-box;
                    }

                    .login-page .k-form .k-input
                    {
                        margin-top: 4px;
                    }

                    .login-page .k-form-buttons
                    {
                        flex-direction: column-reverse;
                        gap: 12px;
                    }

                    .login-page .k-form-buttons .k-button,
                    .login-page .forgot-password,
                    .login-page .k-input
                    {
                        width: 100%;
                    }
                }
            `}
      </style>
    </div>
  );
};

//export const LoginPage = {Component: Login};
